/**
 * TruBeauté — image quality pre-check.
 *
 * Runs BEFORE we send a selfie to GPT-4o so we can:
 *   1. Save expensive LLM tokens on photos that obviously won't work
 *   2. Give the user kind, specific feedback ("a little brighter, please")
 *   3. Avoid the bad-lighting → bad-routine spiral
 *
 * Checks:
 *   - exactly 1 face detected (not 0, not 2+) via MediaPipe FaceLandmarker
 *   - average brightness in a sane range (not a black box, not blown out)
 *   - blur score below a threshold (Laplacian-style edge variance)
 *
 * Returns a structured report. The caller decides how to surface it.
 *
 * Performance: lazy-loads MediaPipe + the face model on first call only,
 * then caches both.
 */

const MEDIAPIPE_WASM =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm";
// Bundled locally — see LiveCoach.jsx for the rationale (faster cold
// start on Capacitor, no slow googleapis fetch).
const FACE_MODEL_URL = `${process.env.PUBLIC_URL || ""}/models/face_landmarker.task`;

let _landmarkerPromise = null;

const _getLandmarker = () => {
  if (_landmarkerPromise) return _landmarkerPromise;
  _landmarkerPromise = (async () => {
    const { FilesetResolver, FaceLandmarker } = await import(
      "@mediapipe/tasks-vision"
    );
    const fileset = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM);
    return FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: FACE_MODEL_URL, delegate: "GPU" },
      runningMode: "IMAGE",
      numFaces: 2, // detect up to 2 so we can flag "more than 1 person"
      outputFaceBlendshapes: false,
    });
  })();
  return _landmarkerPromise;
};

/**
 * Decode a base64 data URL into an HTMLImageElement.
 */
const _loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });

/**
 * Sample brightness + blur from a downscaled grayscale version of the
 * image. We work at 200px wide for speed (≈ 30ms on a laptop).
 */
const _measureBrightnessAndBlur = (img) => {
  const targetW = 200;
  const scale = targetW / img.naturalWidth;
  const w = targetW;
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  // Brightness: mean of luminance (Rec. 601). 0=black, 255=white.
  let lumSum = 0;
  const lum = new Float32Array(w * h);
  for (let i = 0, j = 0; i < data.length; i += 4, j += 1) {
    const v = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lum[j] = v;
    lumSum += v;
  }
  const brightness = lumSum / lum.length; // 0..255

  // Blur: variance of a 3×3 Laplacian. High variance = sharp edges.
  // We sum |L| for each interior pixel then take its variance.
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v =
        4 * lum[i] - lum[i - 1] - lum[i + 1] - lum[i - w] - lum[i + w];
      sum += v;
      sumSq += v * v;
      count += 1;
    }
  }
  const mean = sum / count;
  const variance = sumSq / count - mean * mean;

  return { brightness, blurVariance: variance };
};

/**
 * Run all quality checks against a base64-encoded JPEG/PNG/WEBP image.
 *
 * Returns:
 *   {
 *     ok: boolean,
 *     faceCount: number,
 *     brightness: number,         // 0..255
 *     blurVariance: number,       // higher = sharper
 *     issues: [
 *       { code, severity: "warn"|"error", message }
 *     ]
 *   }
 *
 * The caller can show only ERRORS as blockers and use WARNINGS for soft
 * feedback ("a little brighter would be lovely…").
 */
export const checkImageQuality = async ({ base64, mime }) => {
  const dataUrl = `data:${mime || "image/jpeg"};base64,${base64}`;
  const img = await _loadImage(dataUrl);
  const { brightness, blurVariance } = _measureBrightnessAndBlur(img);

  let faceCount = 0;
  try {
    const lm = await _getLandmarker();
    // FaceLandmarker.detect accepts an HTMLImageElement.
    const result = lm.detect(img);
    faceCount = result?.faceLandmarks?.length || 0;
  } catch (e) {
    // If MediaPipe fails (rare — usually CDN/CORS), don't block the user.
    // We surface a soft warning so they know we couldn't confirm a face.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[imageQuality] face detection failed:", e?.message || e);
    }
    faceCount = -1; // sentinel: detector unavailable
  }

  const issues = [];

  if (faceCount === 0) {
    issues.push({
      code: "no_face",
      severity: "error",
      message: "We couldn't find a face in this photo. Try centering yourself in the frame.",
    });
  } else if (faceCount > 1) {
    issues.push({
      code: "multiple_faces",
      severity: "error",
      message: "More than one face is in the frame — your routine works best as a solo selfie.",
    });
  }

  if (brightness < 60) {
    issues.push({
      code: "too_dark",
      severity: "error",
      message: "It's a touch dark in here. Move toward soft window light and try again.",
    });
  } else if (brightness < 90) {
    issues.push({
      code: "low_light",
      severity: "warn",
      message: "A little more light would help Trouvé see your undertones.",
    });
  } else if (brightness > 230) {
    issues.push({
      code: "overexposed",
      severity: "warn",
      message: "The light is a little harsh — try softening it for a more accurate read.",
    });
  }

  if (blurVariance < 80) {
    issues.push({
      code: "too_blurry",
      severity: "error",
      message: "This photo is a bit blurry. Hold steady, tap to focus, and try again.",
    });
  } else if (blurVariance < 160) {
    issues.push({
      code: "soft_focus",
      severity: "warn",
      message: "A slightly sharper photo will help Trouvé see fine details.",
    });
  }

  const hasError = issues.some((i) => i.severity === "error");
  return {
    ok: !hasError,
    faceCount,
    brightness: Math.round(brightness),
    blurVariance: Math.round(blurVariance),
    issues,
  };
};
